import React, { useState, useEffect } from 'react';
import { menusAPI, subMenusAPI } from './api';

function SubMenuManagement() {
  const [menus, setMenus] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [subMenus, setSubMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSubMenu, setEditingSubMenu] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', image_url: '', external_link: '' });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadMenus();
  }, []);

  const loadMenus = async () => {
    try {
      const response = await menusAPI.getAll();
      const menuData = response.data.data || [];
      setMenus(menuData);
      if (menuData.length > 0) {
        setSelectedMenuId(menuData[0].id);
        loadSubMenus(menuData[0].id);
      }
    } catch (error) {
      console.error('Failed to load menus:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubMenus = async (menuId) => {
    try {
      const response = await subMenusAPI.getAll(menuId);
      const subMenuData = response.data.data || [];
      setSubMenus(subMenuData);
    } catch (error) {
      console.error('Failed to load sub menus:', error);
    }
  };

  const handleMenuChange = (menuId) => {
    setSelectedMenuId(menuId);
    loadSubMenus(menuId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = formData.image_url;
      
      if (selectedFile) {
        const imageFormData = new FormData();
        imageFormData.append("image", selectedFile);
        
        const uploadResponse = await fetch("https://chatpanel.my.id/api/upload/carousel", {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("token")}`
          },
          body: imageFormData
        });
        
        const uploadData = await uploadResponse.json();
        if (uploadData.success) {
          imageUrl = uploadData.imageUrl;
        }
      }

      const submitData = { ...formData, image_url: imageUrl };
      
      if (editingSubMenu) {
        await subMenusAPI.update(editingSubMenu.id, { ...submitData, is_active: 1 });
      } else {
        await subMenusAPI.create({ ...submitData, menu_id: selectedMenuId });
      }
      setShowModal(false);
      setFormData({ title: "", description: "", image_url: "", external_link: "" });
      setSelectedFile(null);
      setEditingSubMenu(null);
      loadSubMenus(selectedMenuId);
    } catch (error) {
      console.error("Failed to save sub menu:", error);
      alert("Failed to save: " + error.message);
    }
  };

  const handleEdit = (subMenu) => {
    setEditingSubMenu(subMenu);
    setFormData({ title: subMenu.title, description: subMenu.description, image_url: subMenu.image_url || '', external_link: subMenu.external_link || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sub menu?')) return;
    try {
      await subMenusAPI.delete(id);
      loadSubMenus(selectedMenuId);
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const moveUp = async (subMenu, index) => {
    if (index === 0) return;
    const prevSubMenu = subMenus[index - 1];
    
    try {
      await subMenusAPI.reorder([
        { id: subMenu.id, display_order: prevSubMenu.display_order },
        { id: prevSubMenu.id, display_order: subMenu.display_order }
      ]);
      await loadSubMenus(selectedMenuId);
    } catch (error) {
      console.error('Failed to reorder:', error);
      alert('Failed to reorder sub menus');
    }
  };

  const moveDown = async (subMenu, index) => {
    if (index === subMenus.length - 1) return;
    const nextSubMenu = subMenus[index + 1];
    
    try {
      await subMenusAPI.reorder([
        { id: subMenu.id, display_order: nextSubMenu.display_order },
        { id: nextSubMenu.id, display_order: subMenu.display_order }
      ]);
      await loadSubMenus(selectedMenuId);
    } catch (error) {
      console.error('Failed to reorder:', error);
      alert('Failed to reorder sub menus');
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        color: '#666'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '12px' }}>📋</div>
          <p>Loading sub menus...</p>
        </div>
      </div>
    );
  }

  const selectedMenu = menus.find(m => m.id === selectedMenuId);

  return (
    <div className="fade-in-up">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '32px'
      }}>
        <div>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '700',
            marginBottom: '8px',
            color: '#1a1a1a'
          }}>Sub Menu Management</h1>
          <p style={{
            fontSize: '16px',
            color: '#666666',
            margin: 0
          }}>Create and organize sub menus for each parent menu.</p>
        </div>
        <button
          onClick={() => {
            setShowModal(true);
            setEditingSubMenu(null);
            setFormData({ title: '', description: '', image_url: '', external_link: '' });
            setSelectedFile(null);
          }}
          style={{
            background: 'linear-gradient(135deg, #700000 0%, #a30021 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 12px rgba(112, 0, 0, 0.2)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(112, 0, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(112, 0, 0, 0.2)';
          }}
        >
          <span style={{ fontSize: '18px' }}>➕</span>
          Add Sub Menu
        </button>
      </div>

      <div className="glass-card" style={{ marginBottom: '24px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px'
        }}>
          <span style={{ fontSize: '24px' }}>📂</span>
          <div>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '700',
              margin: 0,
              color: '#1a1a1a'
            }}>Select Parent Menu</h2>
            <p style={{
              fontSize: '13px',
              color: '#666',
              margin: '4px 0 0 0'
            }}>Choose which menu to manage sub items for</p>
          </div>
        </div>
        <select
          value={selectedMenuId || ''}
          onChange={(e) => handleMenuChange(Number(e.target.value))}
          style={{
            width: '100%',
            padding: '12px 16px',
            border: '2px solid rgba(112, 0, 0, 0.1)',
            borderRadius: '12px',
            fontSize: '15px',
            fontWeight: '500',
            color: '#1a1a1a',
            background: 'rgba(255, 255, 255, 0.5)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            outline: 'none'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#700000';
            e.target.style.background = 'rgba(255, 255, 255, 0.8)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(112, 0, 0, 0.1)';
            e.target.style.background = 'rgba(255, 255, 255, 0.5)';
          }}
        >
          {menus.map(menu => (
            <option key={menu.id} value={menu.id}>
              {menu.icon} {menu.title}
            </option>
          ))}
        </select>
      </div>

      <div className="glass-card">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px'
        }}>
          <span style={{ fontSize: '24px' }}>📝</span>
          <div>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              margin: 0,
              color: '#1a1a1a'
            }}>Sub Menus for: {selectedMenu?.title}</h2>
            <p style={{
              fontSize: '13px',
              color: '#666',
              margin: '4px 0 0 0'
            }}>{subMenus.length} item{subMenus.length !== 1 ? 's' : ''} total</p>
          </div>
        </div>

        {subMenus.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#999'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px', opacity: 0.5 }}>📋</div>
            <p style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0', color: '#666' }}>
              No sub menus yet
            </p>
            <p style={{ fontSize: '14px', margin: 0, color: '#999' }}>
              Click "Add Sub Menu" to create your first sub menu item
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{
                  borderBottom: '2px solid rgba(112, 0, 0, 0.1)',
                  background: 'rgba(112, 0, 0, 0.03)'
                }}>
                  <th style={{
                    padding: '16px 12px',
                    textAlign: 'left',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#700000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Title</th>
                  <th style={{
                    padding: '16px 12px',
                    textAlign: 'left',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#700000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Description</th>
                  <th style={{
                    padding: '16px 12px',
                    textAlign: 'center',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#700000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Order</th>
                  <th style={{
                    padding: '16px 12px',
                    textAlign: 'center',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#700000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Status</th>
                  <th style={{
                    padding: '16px 12px',
                    textAlign: 'center',
                    fontWeight: '700',
                    fontSize: '13px',
                    color: '#700000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subMenus.map((subMenu, index) => (
                  <tr
                    key={subMenu.id}
                    style={{
                      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(112, 0, 0, 0.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{ fontWeight: '600', color: '#1a1a1a', fontSize: '14px' }}>
                        {subMenu.title}
                      </div>
                      {subMenu.external_link && (
                        <div style={{
                          fontSize: '12px',
                          color: '#666',
                          marginTop: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          🔗 <span style={{ 
                            maxWidth: '200px', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap' 
                          }}>{subMenu.external_link}</span>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{ fontSize: '14px', color: '#666', maxWidth: '300px' }}>
                        {subMenu.description}
                      </div>
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        <button
                          onClick={() => moveUp(subMenu, index)}
                          disabled={index === 0}
                          style={{
                            padding: '6px 10px',
                            background: index === 0 ? 'rgba(0, 0, 0, 0.05)' : 'linear-gradient(135deg, #700000 0%, #a30021 100%)',
                            color: index === 0 ? '#999' : 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            opacity: index === 0 ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (index !== 0) {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 4px 8px rgba(112, 0, 0, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (index !== 0) {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }
                          }}
                          title="Move up"
                        >
                          ▲
                        </button>
                        <span style={{
                          display: 'inline-block',
                          padding: '6px 12px',
                          background: 'rgba(112, 0, 0, 0.1)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '700',
                          color: '#700000',
                          minWidth: '40px',
                          textAlign: 'center'
                        }}>
                          {subMenu.display_order}
                        </span>
                        <button
                          onClick={() => moveDown(subMenu, index)}
                          disabled={index === subMenus.length - 1}
                          style={{
                            padding: '6px 10px',
                            background: index === subMenus.length - 1 ? 'rgba(0, 0, 0, 0.05)' : 'linear-gradient(135deg, #700000 0%, #a30021 100%)',
                            color: index === subMenus.length - 1 ? '#999' : 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: index === subMenus.length - 1 ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            opacity: index === subMenus.length - 1 ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (index !== subMenus.length - 1) {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 4px 8px rgba(112, 0, 0, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (index !== subMenus.length - 1) {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }
                          }}
                          title="Move down"
                        >
                          ▼
                        </button>
                      </div>
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <span style={{
                        padding: '6px 16px',
                        borderRadius: '20px',
                        background: subMenu.is_active ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)' : 'rgba(220, 38, 38, 0.1)',
                        color: subMenu.is_active ? 'white' : '#dc2626',
                        fontSize: '12px',
                        fontWeight: '600',
                        display: 'inline-block'
                      }}>
                        {subMenu.is_active ? '✓ Active' : '✗ Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleEdit(subMenu)}
                          style={{
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, #700000 0%, #a30021 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 4px 8px rgba(112, 0, 0, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDelete(subMenu.id)}
                          style={{
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 4px 8px rgba(220, 38, 38, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
            animation: 'fadeIn 0.3s ease'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowModal(false);
              setEditingSubMenu(null);
            }
          }}
        >
          <div
            className="glass-card"
            style={{
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto',
              animation: 'slideUp 0.3s ease'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '2px solid rgba(112, 0, 0, 0.1)'
            }}>
              <span style={{ fontSize: '28px' }}>
                {editingSubMenu ? '✏️' : '➕'}
              </span>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                margin: 0,
                color: '#1a1a1a'
              }}>
                {editingSubMenu ? 'Edit Sub Menu' : 'Add Sub Menu'}
              </h2>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#1a1a1a',
                  fontSize: '14px'
                }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Enter sub menu title"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid rgba(112, 0, 0, 0.1)',
                    borderRadius: '12px',
                    fontSize: '14px',
                    background: 'rgba(255, 255, 255, 0.5)',
                    transition: 'all 0.3s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#700000';
                    e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(112, 0, 0, 0.1)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.5)';
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#1a1a1a',
                  fontSize: '14px'
                }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter description (optional)"
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid rgba(112, 0, 0, 0.1)',
                    borderRadius: '12px',
                    fontSize: '14px',
                    background: 'rgba(255, 255, 255, 0.5)',
                    transition: 'all 0.3s ease',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: '80px',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#700000';
                    e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(112, 0, 0, 0.1)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.5)';
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#1a1a1a',
                  fontSize: '14px'
                }}>
                  Image
                </label>
                
                {formData.image_url && (
                  <div style={{
                    marginBottom: '12px',
                    padding: '12px',
                    background: 'rgba(112, 0, 0, 0.05)',
                    borderRadius: '12px',
                    border: '1px solid rgba(112, 0, 0, 0.1)'
                  }}>
                    <p style={{
                      fontSize: '12px',
                      color: '#666',
                      marginBottom: '8px',
                      fontWeight: '600'
                    }}>
                      Current Image:
                    </p>
                    <img
                      src={`https://chatpanel.my.id${formData.image_url}`}
                      alt="Current"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid rgba(112, 0, 0, 0.1)'
                      }}
                    />
                  </div>
                )}

                <div style={{
                  position: 'relative',
                  border: '2px dashed rgba(112, 0, 0, 0.2)',
                  borderRadius: '12px',
                  padding: '20px',
                  background: 'rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{ pointerEvents: 'none' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>📤</div>
                    <p style={{
                      color: '#1a1a1a',
                      fontSize: '14px',
                      fontWeight: '600',
                      margin: '0 0 4px 0'
                    }}>
                      {selectedFile ? selectedFile.name : 'Click to upload image'}
                    </p>
                    <p style={{ color: '#666', fontSize: '12px', margin: 0 }}>
                      PNG, JPG, GIF up to 5MB
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#1a1a1a',
                  fontSize: '14px'
                }}>
                  External Link (URL)
                </label>
                <input
                  type="url"
                  value={formData.external_link}
                  onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                  placeholder="https://example.com"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid rgba(112, 0, 0, 0.1)',
                    borderRadius: '12px',
                    fontSize: '14px',
                    background: 'rgba(255, 255, 255, 0.5)',
                    transition: 'all 0.3s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#700000';
                    e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(112, 0, 0, 0.1)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.5)';
                  }}
                />
                <p style={{
                  fontSize: '12px',
                  color: '#666',
                  marginTop: '6px',
                  margin: '6px 0 0 0'
                }}>
                  Optional: Link to external content or website
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '14px 24px',
                    background: 'linear-gradient(135deg, #700000 0%, #a30021 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '15px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(112, 0, 0, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(112, 0, 0, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(112, 0, 0, 0.2)';
                  }}
                >
                  {editingSubMenu ? '💾 Update Sub Menu' : '➕ Create Sub Menu'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingSubMenu(null);
                    setFormData({ title: '', description: '', image_url: '', external_link: '' });
                    setSelectedFile(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '14px 24px',
                    background: 'rgba(0, 0, 0, 0.1)',
                    color: '#666',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '15px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(0, 0, 0, 0.15)';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(0, 0, 0, 0.1)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  ✖️ Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .fade-in-up {
          animation: fadeInUp 0.6s ease;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default SubMenuManagement;
